// src/components/SimulationModal.jsx
// Modal de simulation de rendement locatif. Pré-rempli depuis les hypothèses
// budget globales et les valeurs de la fiche bien. Recalcul live à chaque modif.

import React, { useState, useMemo } from 'react';
import { computeSimulation, SIM_DEFAULTS } from '../utils/simulationUtils';

function fmtEur(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return Number(v).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

function fmtPct(v, digits = 2) {
  if (isNaN(v)) return '—';
  return `${(v * 100).toFixed(digits)} %`;
}

export function SimulationModal({ maison, budget, onSave, onClose }) {
  // Pré-remplissage en cascade : sauvegardé > budget global > defaults
  const initial = {
    purchasePrice: Number(maison.prix) || 0,
    notaryFeesRate: maison.rentSim?.notaryFeesRate ?? SIM_DEFAULTS.notaryFeesRate,
    downPayment: maison.rentSim?.downPayment ?? Number(maison.apport) ?? Number(budget?.apport) ?? 0,
    monthlyRent: maison.rentSim?.monthlyRent ?? Number(maison.loyerEstime) ?? 0,
    monthlyCharges: maison.rentSim?.monthlyCharges ?? 0,
    annualPropertyTax: maison.rentSim?.annualPropertyTax ?? 0,
    vacancyRate: maison.rentSim?.vacancyRate ?? SIM_DEFAULTS.vacancyRate,
    annualMaintenanceRate: maison.rentSim?.annualMaintenanceRate ?? SIM_DEFAULTS.annualMaintenanceRate,
    loanRate: maison.rentSim?.loanRate ?? Number(budget?.tauxInteret) ?? SIM_DEFAULTS.loanRate,
    loanDurationMonths:
      maison.rentSim?.loanDurationMonths ?? Number(budget?.dureeMois) ?? SIM_DEFAULTS.loanDurationMonths,
  };

  const [input, setInput] = useState(initial);
  const result = useMemo(() => computeSimulation(input), [input]);

  const update = (key, value) => {
    const num = value === '' ? 0 : Number(value);
    setInput((prev) => ({ ...prev, [key]: isNaN(num) ? 0 : num }));
  };

  const handleSave = () => {
    onSave?.({ ...maison, rentSim: input });
    onClose?.();
  };

  // Lien Google search pour estimer le loyer (toutes sources)
  const ville = maison.ville || '';
  const cp = maison.codePostal || '';
  const estimateUrl = `https://www.google.com/search?q=${encodeURIComponent(`loyer m2 ${ville} ${cp}`.trim())}`;

  // Couleur cashflow
  const cf = result.monthlyCashflow;
  const cfColor = cf >= 0 ? '#22c55e' : cf >= -300 ? '#f59e0b' : '#ef4444';

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={modal}>
        <div style={header}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#f1f5f9' }}>💰 Simulation de rendement locatif</h2>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              {maison.titre || `${maison.type} · ${maison.ville}`}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>

        <div style={twoCol}>
          {/* COLONNE GAUCHE — Inputs */}
          <div>
            <SectionTitle>Hypothèses</SectionTitle>

            <Field label="Prix d'achat" value={input.purchasePrice} onChange={(v) => update('purchasePrice', v)} unit="€" />
            <Field label="Frais notaire" value={Math.round(input.notaryFeesRate * 1000) / 10} onChange={(v) => update('notaryFeesRate', v / 100)} unit="%" />
            <Field label="Apport" value={input.downPayment} onChange={(v) => update('downPayment', v)} unit="€" />

            <FieldWithLink
              label="Loyer mensuel"
              value={input.monthlyRent}
              onChange={(v) => update('monthlyRent', v)}
              unit="€"
              linkUrl={estimateUrl}
              linkLabel="Estimer ↗"
            />

            <Field label="Charges mensuelles" value={input.monthlyCharges} onChange={(v) => update('monthlyCharges', v)} unit="€" />
            <Field label="Taxe foncière /an" value={input.annualPropertyTax} onChange={(v) => update('annualPropertyTax', v)} unit="€" />
            <Field label="Vacance locative" value={Math.round(input.vacancyRate * 1000) / 10} onChange={(v) => update('vacancyRate', v / 100)} unit="%" />
            <Field label="Entretien" value={Math.round(input.annualMaintenanceRate * 1000) / 10} onChange={(v) => update('annualMaintenanceRate', v / 100)} unit="% /an" />
            <Field label="Taux crédit" value={input.loanRate} onChange={(v) => update('loanRate', v)} unit="%" />
            <Field label="Durée crédit" value={input.loanDurationMonths} onChange={(v) => update('loanDurationMonths', v)} unit="mois" />
          </div>

          {/* COLONNE DROITE — Résultats */}
          <div>
            <SectionTitle>Résultats</SectionTitle>

            <Big label="Rendement brut" value={fmtPct(result.grossYield)} color="#a78bfa" />
            <Big label="Rendement net" value={fmtPct(result.netYield)} color="#a78bfa" sub="(hors fiscalité)" />
            <Big
              label="Cashflow mensuel"
              value={`${fmtEur(result.monthlyCashflow)}/mois`}
              color={cfColor}
              sub={cf >= 0 ? 'auto-financé' : cf >= -300 ? 'effort modéré' : 'effort important'}
            />

            <hr style={hr} />

            <Detail label="Coût total achat" value={fmtEur(result.totalCost)} sub={`prix + ${fmtEur(result.notaryFees)} notaire`} />
            <Detail label="Montant emprunté" value={fmtEur(result.loanAmount)} sub="coût total − apport" />
            <Detail label="Mensualité crédit" value={`${fmtEur(result.monthlyMortgage)}/mois`} />
            <Detail label="Coût total crédit" value={fmtEur(result.totalCreditCost)} sub={`dont ${fmtEur(result.interestPaid)} d'intérêts`} />
            <Detail
              label="Effort sur durée"
              value={fmtEur(result.totalEffort)}
              sub={result.totalEffort < 0 ? 'à sortir de votre poche' : 'cumul net encaissé'}
              color={result.totalEffort < 0 ? '#ef4444' : '#22c55e'}
            />
          </div>
        </div>

        <div style={footer}>
          <button onClick={onClose} style={btnSecondary}>Annuler</button>
          <button onClick={handleSave} style={btnPrimary}>💾 Sauvegarder</button>
        </div>
      </div>
    </div>
  );
}

// --- Sub-components ---

function SectionTitle({ children }) {
  return (
    <h3 style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 0, marginBottom: 14, fontWeight: 600 }}>
      {children}
    </h3>
  );
}

function Field({ label, value, onChange, unit }) {
  return (
    <div style={fieldRow}>
      <label style={fieldLabel}>{label}</label>
      <div style={inputWrap}>
        <input
          type="number"
          value={value === 0 ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
          placeholder="0"
        />
        <span style={unitStyle}>{unit}</span>
      </div>
    </div>
  );
}

function FieldWithLink({ label, value, onChange, unit, linkUrl, linkLabel }) {
  return (
    <div style={fieldRow}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <label style={{ ...fieldLabel, marginBottom: 0 }}>{label}</label>
        <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={inlineLink}>
          {linkLabel}
        </a>
      </div>
      <div style={inputWrap}>
        <input
          type="number"
          value={value === 0 ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
          placeholder="0"
        />
        <span style={unitStyle}>{unit}</span>
      </div>
    </div>
  );
}

function Big({ label, value, color, sub }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Detail({ label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid rgba(55,65,81,0.4)' }}>
      <div>
        <div style={{ fontSize: 12, color: '#d1d5db' }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: '#6b7280' }}>{sub}</div>}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: color || '#f3f4f6', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

// --- Styles ---

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2500,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const modal = {
  background: '#0f0f13', color: '#f1f5f9', borderRadius: 18,
  border: '1px solid rgba(255,255,255,0.12)',
  padding: 24, maxWidth: 920, width: '100%', maxHeight: '90vh', overflow: 'auto',
};
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16 };
const closeBtn = {
  background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff',
  width: 30, height: 30, borderRadius: '50%', fontSize: 14, cursor: 'pointer', flexShrink: 0,
};
const twoCol = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 28 };
const fieldRow = { marginBottom: 11 };
const fieldLabel = { display: 'block', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const inputWrap = {
  display: 'flex', alignItems: 'center',
  background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
};
const inputStyle = {
  flex: 1, background: 'transparent', border: 'none',
  padding: '9px 11px', fontSize: 14, color: '#fff', outline: 'none',
};
const unitStyle = { fontSize: 11, color: '#9ca3af', paddingRight: 11, whiteSpace: 'nowrap' };
const inlineLink = { color: '#60a5fa', textDecoration: 'underline', fontSize: 11, fontWeight: 500 };
const hr = { margin: '16px 0', border: 'none', borderTop: '1px solid rgba(55,65,81,0.5)' };
const footer = {
  display: 'flex', justifyContent: 'flex-end', gap: 10,
  marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(55,65,81,0.4)',
};
const btnSecondary = {
  padding: '9px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
  background: 'transparent', color: '#d1d5db', fontWeight: 500, cursor: 'pointer', fontSize: 13,
};
const btnPrimary = {
  padding: '9px 16px', borderRadius: 8, border: 'none',
  background: '#a78bfa', color: '#0f0f13', fontWeight: 700, cursor: 'pointer', fontSize: 13,
};
